/**
 * BAB Report page (/bab-report) — view of the frozen management-accounting report.
 * The user picks a frozen period; every figure is reproduced from the freeze tables and
 * is never written back. The one write path is the comment table [BabProcess].[Comment],
 * which is shared with the BAB Process App.
 *
 * Drill-down: line → accounts → individual bookings. Comments can be captured on any of
 * those three levels and bubble up: a booking comment also shows on its account and its line.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { RefreshCw, MessageSquare, HelpCircle, Info, Download, Loader2 } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/Toast';
import { useBabReport } from '../hooks/useBabReport';
import { babReportApi, toApiFilters, ALL_BUSINESS_UNITS } from '../services/babReportApi';
import { getBuTheme, formatCompanyLabel } from '../utils/buTheme';
import { BabReportTable } from '../components/bab-report/BabReportTable';
import { BabMtdReportTable } from '../components/bab-report/BabMtdReportTable';
import { BabLineAccountModal, accountKey } from '../components/bab-report/BabLineAccountModal';
import { BabMtdLineAccountModal } from '../components/bab-report/BabMtdLineAccountModal';
import { BabCommentPanel, commentLevel } from '../components/bab-report/BabCommentPanel';
import { BabDetailAccessTab } from '../components/bab-report/BabDetailAccessTab';
import { BabQueryInboxTab } from '../components/bab-report/BabQueryInboxTab';
import { BabCommentInboxTab } from '../components/bab-report/BabCommentInboxTab';
import { isOverdue } from '../components/bab-report/BabQueryPane';
import type {
    BabPeriod, BabReportDimensions, BabLineResult, BabLineDefinition,
    BabMtdMonth, BabComment, BabCommentTarget, BabCommentCount,
    BabQuery, BabQueryCount,
} from '../types/babReport';

const LEVEL_CYCLE = [1, 2, 3, 0]; // 1 → 2 → 3 → All → 1
const MTD_MONTH_OPTIONS = [3, 6, 12, 18];
const HIGHLIGHT_MS = 2500;

type Tab = 'report' | 'queries' | 'comments' | 'detail-access';
const TABS: { key: Tab; label: string }[] = [
    { key: 'report', label: 'Report' },
    { key: 'queries', label: 'My Queries' },
    { key: 'comments', label: 'Comments' },
    { key: 'detail-access', label: 'Detail Access' },
];

const emptyCount = (): BabCommentCount => ({ total: 0, line: 0, account: 0, booking: 0 });
const emptyQueryCount = (): BabQueryCount => ({ total: 0, open: 0, overdue: 0 });

export function BabReportPage() {
    const { canView, canEdit, isLoading: permLoading } = usePagePermissions('/bab-report');
    const { user } = useAuth();
    const { toast } = useToast();
    const { filters, patch } = useBabReport();

    const isAdmin = user?.isAdmin ?? false;
    const [tab, setTab] = useState<Tab>('report');

    const [periods, setPeriods] = useState<BabPeriod[]>([]);
    const [dimensions, setDimensions] = useState<BabReportDimensions | null>(null);
    const [metaLoading, setMetaLoading] = useState(true);

    const [lines, setLines] = useState<BabLineResult[]>([]);
    const [mtdMonthsData, setMtdMonthsData] = useState<BabMtdMonth[]>([]);
    const [mtdLineDefs, setMtdLineDefs] = useState<BabLineDefinition[]>([]);
    const [frozenAt, setFrozenAt] = useState<string | null>(null);
    const [hasAccountDetail, setHasAccountDetail] = useState(true);
    const [reportLoading, setReportLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const [comments, setComments] = useState<BabComment[]>([]);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [panelTarget, setPanelTarget] = useState<BabCommentTarget | null>(null);
    const [panelOpen, setPanelOpen] = useState(false);
    /** Which side of the panel to open on — a deep link lands on 'queries'. */
    const [panelTab, setPanelTab] = useState<'comments' | 'queries'>('comments');

    const [queries, setQueries] = useState<BabQuery[]>([]);
    const [queriesLoading, setQueriesLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    /** ?query=123 from a notification mail; cleared once the pane has opened it. */
    const [focusQueryId, setFocusQueryId] = useState<number | null>(null);
    const [drillLine, setDrillLine] = useState<BabLineResult | null>(null);
    /** MTD drill: the Sum line plus the month column that was clicked. */
    const [mtdDrill, setMtdDrill] = useState<{ line: BabLineDefinition; year: number; month: number } | null>(null);
    const [drillAccount, setDrillAccount] = useState<{ companyCode: string; accountCode: string } | null>(null);
    const [drillTransactionRef, setDrillTransactionRef] = useState<string | null>(null);
    /** Bumped on every jump from a comment so the modal remounts and re-runs its jump-to logic. */
    const [drillNonce, setDrillNonce] = useState(0);
    const [highlightedLineCode, setHighlightedLineCode] = useState<string | null>(null);
    /**
     * A jump requested from the Comments tab, parked until the report of the target period
     * has actually arrived. `navigateToComment` looks the line up in `lines`; firing it
     * against the previous period's rows would just toast "not in the current view".
     */
    const [pendingCommentJump, setPendingCommentJump] = useState<BabComment | null>(null);

    const theme = getBuTheme(filters.selectedBuCode);
    const apiFilters = useMemo(() => toApiFilters(filters), [filters]);

    /**
     * May this user open the BOOKING detail of that source company? Cosmetic — it lets the
     * account modal lock a row instead of clicking into a 403; the server decides.
     * Fail-closed while the dimensions are still loading.
     */
    const canDrillCompany = useCallback((code: string) => {
        const access = dimensions?.detailAccess;
        if (!access) return false;
        return access.unrestricted || access.sourceCompanies.includes((code || '').toLowerCase());
    }, [dimensions]);

    // ---- Load periods + dimensions once ----
    useEffect(() => {
        let active = true;
        setMetaLoading(true);
        Promise.all([babReportApi.getPeriods(), babReportApi.getDimensions()])
            .then(([p, d]) => {
                if (!active) return;
                setPeriods(p);
                setDimensions(d);
                // Always open on the newest frozen BAB. The filter state is remembered within
                // the session, but a stored period must never keep a freshly published month
                // out of view — the newest freeze is what people come here for.
                if (p.length > 0) {
                    patch({ periodYear: p[0].periodYear, periodMonth: p[0].periodMonth });
                }
            })
            .catch(err => { if (active) setError(err instanceof Error ? err.message : 'Failed to load report metadata'); })
            .finally(() => { if (active) setMetaLoading(false); });
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---- Company options depend on the selected BU ----
    const companyOptions = useMemo(() => {
        if (!dimensions) return [];
        const codes = new Set<string>();
        for (const r of dimensions.reports) {
            if (filters.selectedBuCode === 'ALL' || r.buCode === filters.selectedBuCode) codes.add(r.babCompanyCode);
        }
        return Array.from(codes).sort();
    }, [dimensions, filters.selectedBuCode]);

    /**
     * Report-company display names, taken from BabReportingMatrix.ReportingName
     * ("Dolder AG - Specialty Chemicals" → "Dolder AG") so the wording is the BAB Process
     * App's, not a second hard-coded list that can drift from it.
     */
    const companyNames = useMemo(() => {
        const names = new Map<string, string>();
        for (const r of dimensions?.reports ?? []) {
            const name = (r.reportingName || '').split(' - ')[0].trim();
            if (name && !names.has(r.babCompanyCode)) names.set(r.babCompanyCode, name);
        }
        return names;
    }, [dimensions]);

    const reportCompanyLabel = (code: string) => {
        const name = companyNames.get(code);
        return name ? `${name} (${code.toUpperCase()})` : code.toUpperCase();
    };

    const sourceCompanyOptions = useMemo(() => {
        if (!dimensions || filters.selectedBabCompanyCode === 'ALL') return [];
        return dimensions.sourceCompanies
            .filter(s => s.babCompanyCode === filters.selectedBabCompanyCode)
            .map(s => s.sourceCompany);
    }, [dimensions, filters.selectedBabCompanyCode]);

    // ---- Load the report when period / filters / view change ----
    const loadReport = useCallback(async () => {
        if (filters.periodYear == null || filters.periodMonth == null) return;
        setReportLoading(true);
        setError(null);
        try {
            if (filters.viewMode === 'MTD') {
                const res = await babReportApi.getMtdReport(filters.periodYear, filters.periodMonth, filters.mtdMonths, apiFilters);
                setMtdMonthsData(res.months);
                setMtdLineDefs(res.lineDefinitions);
                setFrozenAt(res.frozenAt);
                setHasAccountDetail(res.hasAccountDetail);
            } else {
                const res = await babReportApi.getYtdReport(filters.periodYear, filters.periodMonth, apiFilters);
                setLines(res.lines);
                setFrozenAt(res.frozenAt);
                setHasAccountDetail(res.hasAccountDetail);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load report');
        } finally {
            setReportLoading(false);
        }
    }, [filters.periodYear, filters.periodMonth, filters.viewMode, filters.mtdMonths, apiFilters]);

    useEffect(() => {
        if (!metaLoading) void loadReport();
    }, [loadReport, metaLoading, refreshKey]);

    /**
     * The months whose comments and queries belong on this screen, as a REFERENCE-period
     * window (see reference-period.ts on the API side).
     *
     * YTD: January..selected month — a cumulative figure is made of exactly those months, so a
     * note about March belongs on the June YTD row, carrying its own "Ref: Mar 2026" label.
     * MTD: the column window. Computed arithmetically rather than read off `mtdMonthsData`,
     * which only contains FROZEN months and arrives one request later; a window that is a
     * superset of what is displayed costs nothing and is never short.
     */
    const commentWindow = useMemo(() => {
        if (filters.periodYear == null || filters.periodMonth == null) return undefined;
        if (filters.viewMode === 'YTD') return { fromYear: filters.periodYear, fromMonth: 1 };
        const zero = filters.periodYear * 12 + (filters.periodMonth - 1) - (filters.mtdMonths - 1);
        return { fromYear: Math.floor(zero / 12), fromMonth: (zero % 12) + 1 };
    }, [filters.periodYear, filters.periodMonth, filters.viewMode, filters.mtdMonths]);

    // ---- Comments for the window ----
    const loadComments = useCallback(async () => {
        if (filters.periodYear == null || filters.periodMonth == null) return;
        setCommentsLoading(true);
        try {
            const res = await babReportApi.getComments(
                filters.periodYear, filters.periodMonth, apiFilters, commentWindow
            );
            setComments(res);
        } catch {
            setComments([]);
        } finally {
            setCommentsLoading(false);
        }
    }, [filters.periodYear, filters.periodMonth, apiFilters, commentWindow]);

    useEffect(() => {
        if (!metaLoading) void loadComments();
    }, [loadComments, metaLoading, refreshKey]);

    /**
     * Bubble the counts up: a booking comment counts on its booking, on its account AND on
     * the line — so the report row already shows that somebody explained something below.
     */
    const lineCommentCounts = useMemo(() => {
        const counts: Record<string, BabCommentCount> = {};
        for (const c of comments) {
            if (!c.lineCode) continue;
            const bucket = counts[c.lineCode] ?? (counts[c.lineCode] = emptyCount());
            bucket.total += 1;
            bucket[commentLevel(c)] += 1;
        }
        return counts;
    }, [comments]);

    const accountCommentCounts = useMemo(() => {
        const counts: Record<string, BabCommentCount> = {};
        for (const c of comments) {
            if (!c.accountCode || !c.sourceCompanyCode) continue;
            const key = accountKey(c.sourceCompanyCode, c.accountCode);
            const bucket = counts[key] ?? (counts[key] = emptyCount());
            bucket.total += 1;
            bucket[commentLevel(c)] += 1;
        }
        return counts;
    }, [comments]);

    const commentedTxRefs = useMemo(
        () => new Set(comments.map(c => c.transactionRef).filter((r): r is string => !!r)),
        [comments]
    );

    /**
     * MTD badges: one bucket per (line, reference month), keyed `lineCode|year-month`.
     *
     * No roll-up here, unlike the YTD row badges. A cell is a single month of a single line
     * and there is nothing collapsed underneath it to stand in for — but a comment written on
     * an ACCOUNT or a BOOKING of that line still counts on the cell, which is what makes the
     * marker mean "there is something to read about this month".
     */
    const cellKey = (lineCode: string, year: number, month: number) => `${lineCode}|${year}-${month}`;

    const monthCommentCounts = useMemo(() => {
        const counts: Record<string, BabCommentCount> = {};
        for (const c of comments) {
            if (!c.lineCode) continue;
            const key = cellKey(c.lineCode, c.refPeriodYear, c.refPeriodMonth);
            const bucket = counts[key] ?? (counts[key] = emptyCount());
            bucket.total += 1;
            bucket[commentLevel(c)] += 1;
        }
        return counts;
    }, [comments]);


    // ---- Rueckfragen ("queries") for the period ----
    const loadQueries = useCallback(async () => {
        if (filters.periodYear == null || filters.periodMonth == null) return;
        setQueriesLoading(true);
        try {
            const res = await babReportApi.getQueries(
                filters.periodYear, filters.periodMonth, apiFilters, commentWindow
            );
            setQueries(res);
        } catch {
            setQueries([]);
        } finally {
            setQueriesLoading(false);
        }
    }, [filters.periodYear, filters.periodMonth, apiFilters, commentWindow]);

    useEffect(() => {
        if (!metaLoading) void loadQueries();
    }, [loadQueries, metaLoading, refreshKey]);

    /**
     * The agenda for the closing call. The workbook is built server-side and passes the
     * same booking-detail filter as the list on screen, so the row count matches what
     * this user can see here — and differs, legitimately, from what an admin downloads.
     */
    const exportOpenQueries = async () => {
        if (filters.periodYear == null || filters.periodMonth == null) return;
        setExporting(true);
        try {
            await babReportApi.exportOpenQueries(filters.periodYear, filters.periodMonth);
        } catch (err) {
            toast(err instanceof Error ? err.message : 'The export failed', 'error');
        } finally {
            setExporting(false);
        }
    };

    /**
     * Query counts bubble exactly like comment counts: a booking query also counts on its
     * account and on its line, so a row shows that something is being clarified below it.
     * `open` drives the badge colour, `overdue` turns it red.
     */
    const tallyQuery = (bucket: BabQueryCount, q: BabQuery) => {
        bucket.total += 1;
        if (q.status !== 'Closed' && q.status !== 'Rejected') bucket.open += 1;
        if (isOverdue(q)) bucket.overdue += 1;
    };

    const lineQueryCounts = useMemo(() => {
        const counts: Record<string, BabQueryCount> = {};
        for (const q of queries) {
            if (!q.lineCode) continue;
            tallyQuery(counts[q.lineCode] ?? (counts[q.lineCode] = emptyQueryCount()), q);
        }
        return counts;
    }, [queries]);

    const accountQueryCounts = useMemo(() => {
        const counts: Record<string, BabQueryCount> = {};
        for (const q of queries) {
            if (!q.accountCode || !q.sourceCompanyCode) continue;
            const key = accountKey(q.sourceCompanyCode, q.accountCode);
            tallyQuery(counts[key] ?? (counts[key] = emptyQueryCount()), q);
        }
        return counts;
    }, [queries]);

    const queriedTxRefs = useMemo(
        () => new Set(queries.map(q => q.transactionRef).filter((r): r is string => !!r)),
        [queries]
    );

    /**
     * The account twin of `monthCommentCounts`, for the MTD matrix: one bucket per
     * (company:account, reference month). A booking note counts on its account's cell too.
     */
    const monthAccountCommentCounts = useMemo(() => {
        const counts: Record<string, BabCommentCount> = {};
        for (const c of comments) {
            if (!c.accountCode || !c.sourceCompanyCode) continue;
            const key = `${accountKey(c.sourceCompanyCode, c.accountCode)}|${c.refPeriodYear}-${c.refPeriodMonth}`;
            const bucket = counts[key] ?? (counts[key] = emptyCount());
            bucket.total += 1;
            bucket[commentLevel(c)] += 1;
        }
        return counts;
    }, [comments]);

    /** The query twin of `monthCommentCounts` — one bucket per (line, reference month). */
    const monthQueryCounts = useMemo(() => {
        const counts: Record<string, BabQueryCount> = {};
        for (const q of queries) {
            if (!q.lineCode) continue;
            const key = cellKey(q.lineCode, q.refPeriodYear, q.refPeriodMonth);
            tallyQuery(counts[key] ?? (counts[key] = emptyQueryCount()), q);
        }
        return counts;
    }, [queries]);

    const monthAccountQueryCounts = useMemo(() => {
        const counts: Record<string, BabQueryCount> = {};
        for (const q of queries) {
            if (!q.accountCode || !q.sourceCompanyCode) continue;
            const key = `${accountKey(q.sourceCompanyCode, q.accountCode)}|${q.refPeriodYear}-${q.refPeriodMonth}`;
            tallyQuery(counts[key] ?? (counts[key] = emptyQueryCount()), q);
        }
        return counts;
    }, [queries]);

    /** Header badge: how much is still open in the period on screen. */
    const openQueryTotal = useMemo(
        () => queries.filter(q => q.status !== 'Closed' && q.status !== 'Rejected').length,
        [queries]
    );
    const overdueQueryTotal = useMemo(() => queries.filter(isOverdue).length, [queries]);

    // ---- Handlers ----
    const cycleLevel = () => {
        const idx = LEVEL_CYCLE.indexOf(filters.maxViewLevel);
        const next = LEVEL_CYCLE[(idx + 1) % LEVEL_CYCLE.length];
        patch({ maxViewLevel: next });
    };

    const onBuChange = (bu: string) => {
        // Reset company/source when switching BU so we never keep an invalid combo.
        patch({ selectedBuCode: bu, selectedBabCompanyCode: 'ALL', selectedSourceCompany: '' });
    };
    const onCompanyChange = (company: string) => {
        patch({ selectedBabCompanyCode: company, selectedSourceCompany: '' });
    };

    const handleLineClick = (line: BabLineResult) => {
        if (!hasAccountDetail) {
            toast('Account detail is not available for this period.', 'info');
            return;
        }
        setDrillAccount(null);
        setDrillTransactionRef(null);
        setDrillLine(line);
    };

    /**
     * MTD cell → account x month matrix for that line. Deliberately no booking level below
     * it: bookings are live data and cannot reconcile with the frozen monthly values.
     */
    const handleMtdCellClick = (lineCode: string, cellYear: number, cellMonth: number) => {
        const line = mtdLineDefs.find(l => l.lineCode === lineCode);
        if (!line) return;
        setMtdDrill({ line, year: cellYear, month: cellMonth });
    };

    const openComments = (target: BabCommentTarget | null) => {
        setPanelTarget(target);
        setPanelTab('comments');
        setPanelOpen(true);
    };

    const openQueries = (target: BabCommentTarget | null) => {
        setPanelTarget(target);
        setPanelTab('queries');
        setPanelOpen(true);
    };

    /**
     * Deep link from a notification mail: /bab-report?year=2026&month=6&query=123.
     * The panel opens on the Queries tab and the pane fetches that one query by id, so
     * the link works even if the recipient's remembered period is a different month.
     * Runs once; the parameters are then stripped so a refresh does not reopen it.
     */
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const id = parseInt(params.get('query') || '', 10);
        if (!Number.isInteger(id)) return;

        const y = parseInt(params.get('year') || '', 10);
        const m = parseInt(params.get('month') || '', 10);
        if (Number.isInteger(y) && Number.isInteger(m)) {
            patch({ periodYear: y, periodMonth: m });
        }
        setFocusQueryId(id);
        setPanelTarget(null);
        setPanelTab('queries');
        setPanelOpen(true);
        window.history.replaceState({}, '', window.location.pathname);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openLineComments = (line: BabLineResult, rolledUpLineCodes?: string[]) =>
        openComments({
            level: 'line',
            lineCode: line.lineCode,
            lineName: line.lineName,
            rolledUpLineCodes: rolledUpLineCodes?.length ? rolledUpLineCodes : undefined,
        });

    const openLineQueries = (line: BabLineResult, rolledUpLineCodes?: string[]) =>
        openQueries({
            level: 'line',
            lineCode: line.lineCode,
            lineName: line.lineName,
            rolledUpLineCodes: rolledUpLineCodes?.length ? rolledUpLineCodes : undefined,
        });

    /**
     * MTD: a note on ONE month of a line. The month is not a guess here — it is the cell the
     * user clicked, and it travels to the server as the reference period.
     */
    const openCellComments = (lineCode: string, year: number, month: number) => {
        const def = mtdLineDefs.find(l => l.lineCode === lineCode);
        openComments({
            level: 'line',
            lineCode,
            lineName: def?.lineName,
            refPeriodYear: year,
            refPeriodMonth: month,
        });
    };

    const openCellQueries = (lineCode: string, year: number, month: number) => {
        const def = mtdLineDefs.find(l => l.lineCode === lineCode);
        openQueries({
            level: 'line',
            lineCode,
            lineName: def?.lineName,
            refPeriodYear: year,
            refPeriodMonth: month,
        });
    };

    /**
     * MTD matrix: a note on ONE account in ONE month. `buCode: ALL` because a matrix row spans
     * every BU of its company — the server validates the anchor with that same widening and
     * stores it as NULL, which is what "not narrowed to one BU" already means everywhere.
     */
    const mtdAccountTarget = (
        a: { companyCode: string; accountCode: string; accountName: string },
        year: number, month: number
    ): BabCommentTarget => ({
        level: 'account',
        lineCode: mtdDrill?.line.lineCode ?? '',
        lineName: mtdDrill?.line.lineName,
        companyCode: a.companyCode,
        buCode: ALL_BUSINESS_UNITS,
        accountCode: a.accountCode,
        accountName: a.accountName,
        refPeriodYear: year,
        refPeriodMonth: month,
    });

    /** Clicking a comment card takes you to the row it was written on. */
    const navigateToComment = (c: BabComment) => {
        if (!c.lineCode) return;

        // The drill-down this jumps into is the YTD one, and its rows only exist after a YTD
        // load. In a session that opened straight into MTD there are none — switch the view
        // and park the jump rather than tell the user their own comment is "not in view".
        if (lines.length === 0) {
            patch({ viewMode: 'YTD' });
            setPendingCommentJump(c);
            return;
        }

        const line = lines.find(l => l.lineCode === c.lineCode);
        if (!line) {
            toast('That comment belongs to a line which is not in the current view.', 'info');
            return;
        }

        const level = commentLevel(c);
        if (level === 'line') {
            setPanelOpen(false);
            setDrillLine(null);
            // The row may be collapsed away by the level filter — show all levels so the
            // highlight actually lands on something visible.
            if (filters.maxViewLevel !== 0 && (line.viewLevel ?? 0) > filters.maxViewLevel) {
                patch({ maxViewLevel: 0 });
            }
            setHighlightedLineCode(c.lineCode);
            window.setTimeout(() => setHighlightedLineCode(null), HIGHLIGHT_MS);
            return;
        }

        if (!hasAccountDetail || line.operator !== 'Sum') {
            toast('The account detail for that comment is not available in this period.', 'info');
            return;
        }

        // A booking comment of a company this user may not drill into should never reach
        // the client (get-comments filters them out). Guard anyway — a regression there
        // must not turn this jump into a way around the lock.
        if (level === 'booking' && !canDrillCompany(c.sourceCompanyCode ?? '')) {
            toast('Booking detail for that company is not available to you.', 'info');
            return;
        }

        setPanelOpen(false);
        setDrillAccount(c.sourceCompanyCode && c.accountCode
            ? { companyCode: c.sourceCompanyCode, accountCode: c.accountCode }
            : null);
        setDrillTransactionRef(level === 'booking' ? c.transactionRef : null);
        setDrillLine(line);
        setDrillNonce(n => n + 1);
    };

    // Run a parked Comments-tab jump as soon as the target period's rows are on screen.
    useEffect(() => {
        if (!pendingCommentJump || reportLoading || lines.length === 0) return;
        navigateToComment(pendingCommentJump);
        setPendingCommentJump(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingCommentJump, reportLoading, lines]);

    /**
     * Clicking a query card takes you to the position it hangs on — the same jump the
     * comment cards make, expressed against the query's own anchor columns.
     */
    const navigateToQuery = (q: BabQuery) => {
        if (!q.lineCode) return;
        const line = lines.find(l => l.lineCode === q.lineCode);
        if (!line) {
            toast('That query belongs to a line which is not in the current view.', 'info');
            return;
        }

        if (q.anchorLevel === 'line') {
            setPanelOpen(false);
            setDrillLine(null);
            if (filters.maxViewLevel !== 0 && (line.viewLevel ?? 0) > filters.maxViewLevel) {
                patch({ maxViewLevel: 0 });
            }
            setHighlightedLineCode(q.lineCode);
            window.setTimeout(() => setHighlightedLineCode(null), HIGHLIGHT_MS);
            return;
        }

        if (!hasAccountDetail || line.operator !== 'Sum') {
            toast('The account detail for that query is not available in this period.', 'info');
            return;
        }

        // Booking queries outside the scope never reach the client (the list endpoint
        // filters them in SQL). Guard anyway — a regression there must not turn this
        // jump into a way around the lock.
        if (q.anchorLevel === 'booking' && !canDrillCompany(q.sourceCompanyCode ?? '')) {
            toast('Booking detail for that company is not available to you.', 'info');
            return;
        }

        setPanelOpen(false);
        setDrillAccount(q.sourceCompanyCode && q.accountCode
            ? { companyCode: q.sourceCompanyCode, accountCode: q.accountCode }
            : null);
        setDrillTransactionRef(q.anchorLevel === 'booking' ? q.transactionRef : null);
        setDrillLine(line);
        setDrillNonce(n => n + 1);
    };

    const createComment = async (target: BabCommentTarget, text: string) => {
        if (filters.periodYear == null || filters.periodMonth == null) return;
        await babReportApi.createComment(filters.periodYear, filters.periodMonth, target, text, apiFilters);
        await loadComments();
        toast('Comment saved.', 'success');
    };

    const updateComment = async (commentId: number, text: string) => {
        await babReportApi.updateComment(commentId, text);
        await loadComments();
        toast('Comment updated.', 'success');
    };

    const deleteComment = async (commentId: number) => {
        await babReportApi.deleteComment(commentId);
        await loadComments();
        toast('Comment deleted.', 'success');
    };

    if (permLoading) {
        return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
    }
    if (!canView) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    You do not have permission to view the BAB Report.
                </div>
            </div>
        );
    }

    const visibleTabs = TABS.filter(t => t.key !== 'detail-access' || isAdmin);
    const levelLabel = filters.maxViewLevel === 0 ? 'All Levels' : `Level ${filters.maxViewLevel}`;
    const selectClass = 'border rounded-md px-3 py-1.5 text-sm bg-white';
    const labelClass = 'text-xs font-medium text-gray-500 block mb-1';
    const unitNote = (filters.amountScale === 'thousands'
        ? 'All amounts in EUR, shown in thousands.'
        : 'All amounts in EUR.')
        + (filters.signMode === 'signed' ? ' Income positive, costs negative.' : '');

    return (
        <div className="p-6 space-y-4">
            {/* Header */}
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">BAB Report <span className="text-base font-normal text-gray-400">(EUR)</span></h1>
                    <p className="text-sm text-gray-500">
                        Frozen management-accounting report (read-only).
                        {frozenAt && <> Frozen at {new Date(frozenAt).toLocaleString('de-CH')}.</>}
                        {' '}{unitNote}
                    </p>
                </div>
                {/* Report-only actions — a Refresh button on the admin tab would refresh
                    a report nobody is looking at. */}
                {tab === 'report' && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => openComments(null)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50"
                        >
                            <MessageSquare className="h-4 w-4" /> Comments
                        </button>
                        <button
                            onClick={() => openQueries(null)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50"
                        >
                            <HelpCircle className="h-4 w-4" /> Queries
                            {openQueryTotal > 0 && (
                                <span className={`text-xs rounded-full px-1.5 py-0.5 ${overdueQueryTotal > 0
                                    ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
                                    {openQueryTotal}
                                </span>
                            )}
                        </button>
                        {/* The agenda for the closing call. Only offered when there is
                            something to talk about — an empty workbook is a wasted click. */}
                        {openQueryTotal > 0 && (
                            <button
                                onClick={exportOpenQueries}
                                disabled={exporting}
                                title="Download the open queries of this period, grouped by company"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50 disabled:opacity-50"
                            >
                                {exporting
                                    ? <Loader2 className="h-4 w-4 animate-spin" />
                                    : <Download className="h-4 w-4" />}
                                Export open points
                            </button>
                        )}
                        <button
                            onClick={() => setRefreshKey(k => k + 1)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50"
                        >
                            <RefreshCw className={`h-4 w-4 ${reportLoading ? 'animate-spin' : ''}`} /> Refresh
                        </button>
                    </div>
                )}
            </div>

            {/* Tabs — "Detail Access" is admin-only (both endpoints behind it are too). */}
            {visibleTabs.length > 1 && (
                <div className="flex flex-wrap gap-1 border-b border-gray-200">
                    {visibleTabs.map(t => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${tab === t.key
                                ? 'border-blue-500 text-blue-700'
                                : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            )}

            {tab === 'detail-access' && isAdmin && <BabDetailAccessTab />}

            {tab === 'comments' && (
                <BabCommentInboxTab
                    periods={periods}
                    onOpen={item => {
                        // A comment can be about a month other than the one on screen, so the
                        // jump switches to the period it was WRITTEN in — that is the frozen
                        // report the drill-down can actually reproduce — and then hands over
                        // to the same navigation the panel cards use.
                        patch({ periodYear: item.periodYear, periodMonth: item.periodMonth, viewMode: 'YTD' });
                        setPendingCommentJump(item);
                        setTab('report');
                    }}
                />
            )}

            {tab === 'queries' && (
                <BabQueryInboxTab
                    onOpen={item => {
                        // Same route as the mail's deep link: switch to the period the
                        // query hangs on, then let the pane fetch it by id.
                        patch({ periodYear: item.periodYear, periodMonth: item.periodMonth });
                        setFocusQueryId(item.queryId);
                        setPanelTarget(null);
                        setPanelTab('queries');
                        setPanelOpen(true);
                        setTab('report');
                    }}
                />
            )}

            {tab === 'report' && (<>
            {/* Filter bar */}
            <div className="bg-white rounded-lg shadow-sm border p-4">
                <div className="flex flex-wrap gap-4 items-end">
                    <div>
                        <label className={labelClass}>Period</label>
                        <select
                            className={selectClass}
                            value={filters.periodYear != null ? `${filters.periodYear}-${filters.periodMonth}` : ''}
                            onChange={e => {
                                const [y, m] = e.target.value.split('-').map(Number);
                                patch({ periodYear: y, periodMonth: m });
                            }}
                        >
                            {periods.map(p => (
                                <option key={p.label} value={`${p.periodYear}-${p.periodMonth}`}>{p.label}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className={labelClass}>Business Unit</label>
                        <select className={selectClass} value={filters.selectedBuCode} onChange={e => onBuChange(e.target.value)}>
                            <option value="ALL">All Business Units</option>
                            {dimensions?.businessUnits.map(b => (
                                <option key={b.buCode} value={b.buCode}>{b.buCode} — {b.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className={labelClass}>Report Company</label>
                        <select className={selectClass} value={filters.selectedBabCompanyCode} onChange={e => onCompanyChange(e.target.value)}>
                            <option value="ALL">All Reports</option>
                            {companyOptions.map(c => (
                                <option key={c} value={c}>{reportCompanyLabel(c)}</option>
                            ))}
                        </select>
                    </div>

                    {filters.selectedBabCompanyCode !== 'ALL' && sourceCompanyOptions.length > 1 && (
                        <div>
                            <label className={labelClass}>Source Company</label>
                            <select className={selectClass} value={filters.selectedSourceCompany} onChange={e => patch({ selectedSourceCompany: e.target.value })}>
                                <option value="">All Sources</option>
                                {sourceCompanyOptions.map(s => (
                                    <option key={s} value={s}>{formatCompanyLabel(s)}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="ml-auto flex items-end gap-2">
                        {/* View mode */}
                        <div className="inline-flex rounded-md border overflow-hidden">
                            {(['YTD', 'MTD'] as const).map(vm => (
                                <button
                                    key={vm}
                                    onClick={() => patch({ viewMode: vm })}
                                    className={`px-3 py-1.5 text-sm ${filters.viewMode === vm ? 'bg-blue-600 text-white' : 'bg-white hover:bg-gray-50'}`}
                                >
                                    {vm}
                                </button>
                            ))}
                        </div>

                        {filters.viewMode === 'MTD' && (
                            <select className={selectClass} value={filters.mtdMonths} onChange={e => patch({ mtdMonths: Number(e.target.value) })}>
                                {MTD_MONTH_OPTIONS.map(n => <option key={n} value={n}>{n} months</option>)}
                            </select>
                        )}

                        <button onClick={cycleLevel} className="px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50 whitespace-nowrap">
                            {levelLabel}
                        </button>

                        {/* Unit — affects this overview only, the drill-down modals are always full EUR. */}
                        <div className="inline-flex rounded-md border overflow-hidden" title="Applies to the report below; drill-down details are always shown in full EUR">
                            {([['actual', 'EUR'], ['thousands', "EUR '000"]] as const).map(([value, label]) => (
                                <button
                                    key={value}
                                    onClick={() => patch({ amountScale: value })}
                                    className={`px-3 py-1.5 text-sm whitespace-nowrap ${filters.amountScale === value ? 'bg-blue-600 text-white' : 'bg-white hover:bg-gray-50'}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        {/* Signs. Unlike the unit toggle this one DOES reach into the drill-down
                            modals — a cost that is negative in the grid and positive one click
                            deeper would just look like a bug. */}
                        <div
                            className="inline-flex rounded-md border overflow-hidden"
                            title="BAB: every amount positive, the way the report is read on paper. +/−: income and results positive, costs negative, so the column adds up."
                        >
                            {([['bab', 'BAB'], ['signed', '+/−']] as const).map(([value, label]) => (
                                <button
                                    key={value}
                                    onClick={() => patch({ signMode: value })}
                                    className={`px-3 py-1.5 text-sm whitespace-nowrap ${filters.signMode === value ? 'bg-blue-600 text-white' : 'bg-white hover:bg-gray-50'}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {!hasAccountDetail && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                        <Info className="h-4 w-4 flex-shrink-0" />
                        Account-level drill-down is not available for this period (it was frozen without account detail).
                    </div>
                )}
            </div>

            {/* Report */}
            <div className="bg-white rounded-lg shadow-sm border">
                {error ? (
                    <div className="m-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
                ) : reportLoading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    </div>
                ) : filters.viewMode === 'MTD' ? (
                    <BabMtdReportTable
                        months={mtdMonthsData}
                        lineDefinitions={mtdLineDefs}
                        maxViewLevel={filters.maxViewLevel}
                        scale={filters.amountScale}
                        signMode={filters.signMode}
                        theme={theme}
                        onCellClick={handleMtdCellClick}
                        commentCounts={monthCommentCounts}
                        onCommentClick={openCellComments}
                        queryCounts={monthQueryCounts}
                        onQueryClick={openCellQueries}
                    />
                ) : (
                    <BabReportTable
                        lines={lines}
                        maxViewLevel={filters.maxViewLevel}
                        scale={filters.amountScale}
                        signMode={filters.signMode}
                        onLineClick={hasAccountDetail ? handleLineClick : undefined}
                        commentCounts={lineCommentCounts}
                        onCommentClick={openLineComments}
                        queryCounts={lineQueryCounts}
                        onQueryClick={openLineQueries}
                        theme={theme}
                        highlightedLineCode={highlightedLineCode}
                    />
                )}
            </div>

            {/* Drill-down modal (YTD, Sum lines only) */}
            {drillLine && filters.periodYear != null && filters.periodMonth != null && (
                <BabLineAccountModal
                    key={`${drillLine.lineCode}-${drillNonce}`}
                    line={drillLine}
                    year={filters.periodYear}
                    month={filters.periodMonth}
                    apiFilters={apiFilters}
                    signMode={filters.signMode}
                    showBuColumn={filters.selectedBuCode === 'ALL'}
                    canDrillCompany={canDrillCompany}
                    commentCounts={accountCommentCounts}
                    commentedTxRefs={commentedTxRefs}
                    onCommentClick={openComments}
                    queryCounts={accountQueryCounts}
                    queriedTxRefs={queriedTxRefs}
                    onQueryClick={openQueries}
                    initialAccount={drillAccount}
                    initialTransactionRef={drillTransactionRef}
                    onClose={() => { setDrillLine(null); setDrillAccount(null); setDrillTransactionRef(null); }}
                />
            )}

            {/* Drill-down modal (MTD, Sum lines only) — account x month, no booking level */}
            {mtdDrill && filters.periodYear != null && filters.periodMonth != null && (
                <BabMtdLineAccountModal
                    line={mtdDrill.line}
                    year={filters.periodYear}
                    month={filters.periodMonth}
                    months={filters.mtdMonths}
                    apiFilters={apiFilters}
                    signMode={filters.signMode}
                    canDrillCompany={canDrillCompany}
                    commentedTxRefs={commentedTxRefs}
                    onCommentClick={openComments}
                    queriedTxRefs={queriedTxRefs}
                    onQueryClick={openQueries}
                    monthAccountCommentCounts={monthAccountCommentCounts}
                    monthAccountQueryCounts={monthAccountQueryCounts}
                    accountMonthTarget={mtdAccountTarget}
                    highlight={{ year: mtdDrill.year, month: mtdDrill.month }}
                    onClose={() => setMtdDrill(null)}
                />
            )}

            {/* Comment panel */}
            <BabCommentPanel
                isOpen={panelOpen}
                onClose={() => setPanelOpen(false)}
                target={panelTarget}
                comments={comments}
                loading={commentsLoading}
                canEdit={canEdit}
                currentUserEmail={user?.email ?? ''}
                isAdmin={user?.isAdmin ?? false}
                onCreate={createComment}
                onUpdate={updateComment}
                onDelete={deleteComment}
                onNavigate={navigateToComment}
                queries={queries}
                queriesLoading={queriesLoading}
                periodYear={filters.periodYear ?? 0}
                periodMonth={filters.periodMonth ?? 0}
                apiFilters={apiFilters}
                onQueriesChanged={loadQueries}
                onCommentsChanged={loadComments}
                onNavigateQuery={navigateToQuery}
                initialTab={panelTab}
                focusQueryId={focusQueryId}
                onFocusHandled={() => setFocusQueryId(null)}
            />
            </>)}
        </div>
    );
}
